/** A space's pages, by their path below `/s/:space/`. */
import type { FunctionComponent } from "preact";
import { match } from "./lib/router.ts";
import type { PageProps } from "./lib/state.ts";
import { ApiKeys } from "./pages/ApiKeys.tsx";
import { Billing } from "./pages/Billing.tsx";
import { Bookmarks } from "./pages/Bookmarks.tsx";
import { Commit } from "./pages/Commit.tsx";
import { Catalog } from "./pages/catalog/Catalog.tsx";
import { Entities } from "./pages/Entities.tsx";
import { EntityEditor } from "./pages/EntityEditor.tsx";
import { EntityMap } from "./pages/EntityMap.tsx";
import { History } from "./pages/History.tsx";
import { ImportSpace } from "./pages/ImportSpace.tsx";
import { Issues } from "./pages/Issues.tsx";
import { Item } from "./pages/Item.tsx";
import { Items } from "./pages/Items.tsx";
import { Members } from "./pages/Members.tsx";
import { OrgSettings } from "./pages/OrgSettings.tsx";
import { Privacy } from "./pages/Privacy.tsx";
import { Queue } from "./pages/Queue.tsx";
import { Reports } from "./pages/Reports.tsx";
import { Review } from "./pages/Review.tsx";
import { RuleEditor, Rules } from "./pages/Rules.tsx";
import { SpaceConversation } from "./pages/SpaceConversation.tsx";
import { SpaceSettings } from "./pages/SpaceSettings.tsx";
import { SpacesSettings } from "./pages/SpacesSettings.tsx";
import { Trash } from "./pages/Trash.tsx";
import { HeldRedirect, Triage } from "./pages/Triage.tsx";
import { Usage } from "./pages/Usage.tsx";
import { Webhooks } from "./pages/Webhooks.tsx";

const ROUTES: [string, FunctionComponent<PageProps>][] = [
  ["triage", Triage],
  // Unknown senders, once a page of its own: now a tab of the triage area.
  ["held", HeldRedirect],
  ["queue", Queue],
  ["conversation", SpaceConversation],
  ["reports", Reports],
  ["items", Items],
  ["items/:id", Item],
  ["entities", Entities],
  ["entities/map", EntityMap],
  ["entities/catalog", Catalog],
  ["entities/:id", EntityEditor],
  ["issues", Issues],
  ["rules", Rules],
  ["rules/:id", RuleEditor],
  ["reviews/:id", Review],
  ["history", History],
  ["history/:id", Commit],
  ["bookmarks", Bookmarks],
  ["trash", Trash],
  ["settings", SpaceSettings],
  ["settings/org", OrgSettings],
  ["settings/spaces", SpacesSettings],
  ["settings/members", Members],
  ["settings/billing", Billing],
  ["settings/usage", Usage],
  ["settings/keys", ApiKeys],
  ["settings/import", ImportSpace],
  ["settings/hooks", Webhooks],
  ["settings/privacy", Privacy],
];

/** Pages that show items (lists of them, one, their revisions): where others' private items may be among them. */
const SHOW_ITEMS = new Set([
  "triage",
  "queue",
  "items",
  "items/:id",
  "entities/map",
  "issues",
  "reviews/:id",
  "history",
  "history/:id",
  "bookmarks",
  "trash",
]);

/** Pages that say where the org stands with its plan themselves: no plan banner atop them. */
const SAYS_PLAN = new Set(["settings/billing"]);

/**
 * The page for a path below `/s/:space/`, with its parameters, whether it shows items, and whether the plan banner goes
 * atop it (none: no such page).
 */
export function findRoute(
  rest: string,
): { page: FunctionComponent<PageProps>; params: Record<string, string>; items: boolean; plan: boolean } | null {
  for (const [pattern, Page] of ROUTES) {
    const params = match(pattern, rest);
    if (params) {
      return { page: Page, params, items: SHOW_ITEMS.has(pattern), plan: !SAYS_PLAN.has(pattern) };
    }
  }
  return null;
}
